import { toast } from "sonner";

/**
 * Health check periodico del backend.
 * - Mantiene aggiornato lo stato in un modulo esportato (getHealthState).
 * - Emette eventi 'ma:health-change' su window quando lo stato cambia.
 * - NON produce toast ad ogni ping: soltanto quando lo stato passa da OK a KO,
 *   e un toast di "servizio ripristinato" quando torna OK.
 *
 * v1.0.1: verifica solo /api/health (endpoint pubblico). Il probe DB avveniva
 * su /api/health/db, ma quell'endpoint è stato reso admin-only per non esporre
 * dettagli tecnici. La liveness del DB è comunque garantita dal fatto che ogni
 * chiamata autenticata al backend attraversa il DB.
 *
 * Distinzione:
 *   - "up"      : backend risponde OK.
 *   - "network" : backend non raggiungibile (ERR_NETWORK, timeout, 502/504).
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HEALTH_URL = `${BACKEND_URL}/api/health`;
const INTERVAL_MS = 60_000;         // ricontrollo ogni 60s
const INITIAL_DELAY_MS = 4_000;     // primo check DOPO che la pagina è idle
const TIMEOUT_MS = 6_000;

let currentState = { status: "up", message: "", checkedAt: null };
let intervalHandle = null;
let started = false;

export function getHealthState() {
  return { ...currentState };
}

function emitChange(prev, next) {
  const changed = prev.status !== next.status;
  window.dispatchEvent(new CustomEvent("ma:health-change", { detail: { prev, next, changed } }));
  if (!changed) return;
  if (next.status === "up" && prev.status !== "up") {
    try { toast.success("Servizio ripristinato: connessione al server riattivata."); } catch {}
  } else if (next.status === "network") {
    try { toast.error("Server non raggiungibile. Ricontrollo automatico in corso…"); } catch {}
  }
}

async function fetchWithTimeout(url, ms) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    const r = await fetch(url, { method: "GET", signal: ctrl.signal, credentials: "omit" });
    return r;
  } finally {
    clearTimeout(t);
  }
}

async function doCheck() {
  const prev = { ...currentState };
  let next = { status: "up", message: "", checkedAt: new Date().toISOString() };
  try {
    const r = await fetchWithTimeout(HEALTH_URL, TIMEOUT_MS);
    if (!r.ok) {
      next = { ...next, status: "network", message: `HTTP ${r.status}` };
    } else {
      next = { ...next, status: "up", message: "" };
    }
  } catch (e) {
    next = { ...next, status: "network", message: e?.message || "Rete non disponibile" };
  }
  currentState = next;
  emitChange(prev, next);
}

export function startHealthCheck() {
  if (started) return;
  started = true;
  const kickoff = () => {
    setTimeout(() => {
      doCheck();
      intervalHandle = setInterval(doCheck, INTERVAL_MS);
    }, INITIAL_DELAY_MS);
  };
  if (document.readyState === "complete") {
    kickoff();
  } else {
    window.addEventListener("load", kickoff, { once: true });
  }
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && started) doCheck();
  });
  window.addEventListener("online", () => { if (started) doCheck(); });
}

export function stopHealthCheck() {
  if (intervalHandle) clearInterval(intervalHandle);
  intervalHandle = null;
  started = false;
}
